import CareerDetails from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/CareerDetails';
import CareerPlanner from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/CareerPlanner';
import CareerPathPlannerWidget from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/CareerPathPlannerWidget';
import DataBox from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/DataBox';
import OnetDisclaimer from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/OnetDisclaimer';

import useCareerPathwayPlanner from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/useCareerPathwayPlanner';
import React, { useEffect, useRef } from 'react';
import RecommendedSection from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/RecommendedSection';
import { DisclosureTooltip } from '@/components/Tooltip';
import { Category } from '@/types/Mobius';
import ThreeDotLoader from './components/ThreeDotLoader';
import useViewEvents from '@/hooks/useViewEvent';
import uuid from '@/utils/uuid';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';

const CareerPathwayPlanner = () => {
  const hasSubmitted = useRef(false);
  const { eventContext } = useDynamicExperienceContext();
  // Base web element for tracking
  const baseWebElement = {
    elementType: 'tool',
    location: 'career-pathways',
    name: 'career-pathways-v1',
    position: 'career-pathways',
    text: 'Discover my path',
  };

  const viewCorrelationId = useRef(uuid()).current;

  const {
    currentEducationOptions,
    currentRoleOptions,
    targetRoleOptions,
    steps,
    programs,
    selectedCurrentRole,
    selectedTargetRole,
    selectedCurrentEducation,
    selectedFieldOfStudy,
    loading,
    estimatedTimeToComplete,
    averageSalaryIncrease,
    setSelectedCurrentEducation,
    setSelectedFieldOfStudy,
    setSelectedCurrentRole,
    setSelectedTargetRole,
    handleSubmit,
    showPlanner,
    setShowPlanner,
    correlationId,
    categories,
    handleEditDetailsClick,
    studyMatchDestination,
    productCorrelationId,
  } = useCareerPathwayPlanner(viewCorrelationId);

  const baseEventPayload = {
    webElement: baseWebElement,
  };

  const { ref: viewEventRef } = useViewEvents([
    {
      name: 'ProductViewed',
      payload: {
        viewCorrelationId: viewCorrelationId,
        product: {
          brand: null,
          category: null,
          formatSubtype: 'career-pathways-entry',
          formatType: 'dexp-tool-entry',
          location: 'career-pathways',
          name: null,
          productId: null,
          positionEngine: null,
        },
      },
    },
    {
      name: 'ElementViewed',
      payload: {
        ...baseEventPayload,
        correlationId: correlationId || '',
      },
    },
  ]);

  const handleWidgetSubmit = (data: any) => {
    // Call the original handleSubmit function
    handleSubmit();
    // Mark that form has been submitted
    hasSubmitted.current = true;
  };

  // Set showPlanner to true only when loading becomes false after submission
  useEffect(() => {
    if (hasSubmitted.current && !loading) {
      setShowPlanner(true);
    }
  }, [loading, setShowPlanner]);

  // If showPlanner is false, show the widget first
  if (!showPlanner) {
    return (
      <div
        ref={
          !eventContext?.dynamicExperienceEventFlag
            ? (viewEventRef as React.RefObject<HTMLDivElement>)
            : undefined
        }
      >
        <CareerPathPlannerWidget
          categories={categories as Category[]}
          currentEducationOptions={currentEducationOptions}
          currentRoleOptions={currentRoleOptions}
          targetRoleOptions={targetRoleOptions}
          selectedCurrentEducation={selectedCurrentEducation || undefined}
          selectedFieldOfStudy={selectedFieldOfStudy || undefined}
          selectedCurrentRole={selectedCurrentRole || undefined}
          selectedTargetRole={selectedTargetRole || undefined}
          onSubmit={handleWidgetSubmit}
          onCurrentEducationChange={setSelectedCurrentEducation}
          onFieldOfStudyChange={setSelectedFieldOfStudy}
          onCurrentRoleChange={setSelectedCurrentRole}
          onTargetRoleChange={setSelectedTargetRole}
          editOpen={false}
          loading={loading}
          correlationId={correlationId}
          viewCorrelationId={viewCorrelationId}
        />
      </div>
    );
  }

  return (
    <div className='bg-white max-w-4xl mx-auto p-3 md:p-5 border border-gray-200 rounded-2xl shadow-sm not-prose'>
      <div className='max-w-7xl mx-auto'>
        {/* Header Section */}
        <div className='flex justify-between items-center'>
          <span className='text-xl md:text-2xl font-bold text-gray-900'>
            Your Career Path Planner
          </span>
          <DisclosureTooltip
            position={'bottom'}
            label={'Learn more about our partners'}
            className='text-xs font-normal'
          />
        </div>
        <p className='text-gray-600 text-sm md:text-base mt-2 mb-3 md:mb-6'>
          We recommend the path below to achieve your career goals.
        </p>

        {/* Main Content */}
        <div className='flex flex-col gap-6'>
          {/* TODO: This is temporary. Will need to sync with Gabriel and Daniel since they are making a reusable component for this */}
          {/* Career "QDF" */}
          <CareerDetails
            currentEducationOptions={currentEducationOptions}
            categories={categories}
            currentRoleOptions={currentRoleOptions}
            targetRoleOptions={targetRoleOptions}
            selectedCurrentEducation={selectedCurrentEducation}
            selectedFieldOfStudy={selectedFieldOfStudy}
            selectedCurrentRole={selectedCurrentRole}
            selectedTargetRole={selectedTargetRole}
            loading={loading}
            correlationId={correlationId}
            viewCorrelationId={viewCorrelationId}
            onCurrentEducationChange={setSelectedCurrentEducation}
            onFieldOfStudyChange={setSelectedFieldOfStudy}
            onCurrentRoleChange={setSelectedCurrentRole}
            onTargetRoleChange={setSelectedTargetRole}
            onSubmit={handleSubmit}
            onEditClick={() => {}}
            handleEditDetailsClick={handleEditDetailsClick}
          />

          {loading ? (
            <div className='flex items-center justify-center w-full'>
              <ThreeDotLoader />
            </div>
          ) : (
            <div className='flex flex-col lg:flex-row gap-8'>
              <div className='flex-1 md:space-y-4'>
                {/* Career Steps */}
                <CareerPlanner
                  steps={steps}
                  currentRole={selectedCurrentRole?.title || ''}
                  targetRole={selectedTargetRole?.title || ''}
                  correlationId={productCorrelationId}
                />
                {/* Data Boxes */}
                <div className='hidden md:w-full md:flex md:flex-row md:gap-4'>
                  {estimatedTimeToComplete && (
                    <DataBox
                      label='Estimated Time to Complete'
                      value={estimatedTimeToComplete}
                      className='bg-tertiary-50'
                    />
                  )}
                  {averageSalaryIncrease && (
                    <DataBox
                      label='Average Salary Increase'
                      value={averageSalaryIncrease}
                      className='bg-primary-50'
                    />
                  )}
                </div>
              </div>

              {programs.length > 0 && (
                <div className='lg:max-w-[250px]'>
                  <RecommendedSection
                    studyMatchDestination={studyMatchDestination}
                    recommendedItems={programs}
                    title='Recommended Programs'
                    subtitle={selectedTargetRole?.title}
                    correlationId={productCorrelationId}
                    selectedCurrentEducation={selectedCurrentEducation}
                    selectedFieldOfStudy={selectedFieldOfStudy}
                  />
                </div>
              )}
            </div>
          )}
          {/* TODO: Commenting this for MVP */}
          {/* <RecommendedSection
            recommendedItems={resources}
            title='Recommended Resources'
            orientation='horizontal'
          /> */}
          <OnetDisclaimer />
        </div>
      </div>
    </div>
  );
};

export default CareerPathwayPlanner;
